// サーベイ・シーズン作成/編集フォームの項目定義（唯一の出典）。
//
// 画面の「必須／任意」バッジ・登録前バリデーション・エラー時の項目名表示は
// すべてこの表から導出する。項目の必須要否を変えるときはここだけを直せばよい。
// サーバー側スキーマ（models/SurveySeason.js）の required もこの表と一致させること。
//
// 背景: 以前は placeholder に手書きした「（必須）」と、DBスキーマの required と、
// 画面側の検証（存在しなかった）がバラバラで、
//   ・表記は必須なのに実際は任意（シーズン説明・研修名）
//   ・登録に失敗しても何が足りないのか画面に出ない
// という状態だった。定義を1箇所に集約してこれを解消する。

// 「結果の表示方法（公開選択）」を持つサーベイ。
// 30010 は e3_option が「パーソナル診断あり」のときのみ公開選択が出る。
export const PUBLISH_SETTING_SURVEY_CODES = [
  '10010',
  '50010',
  '50020',
  '50030',
  '50040',
  '40010',
  '60020',
];
export const E3_SURVEY_CODE = '30010';
// 表示領域選択記号（コース構成）を持つサーベイ
export const COURSE_PART_VIEW_SURVEY_CODES = ['90101', '90501', '90502'];

// 公開選択が画面に出る条件
export const hasPublishSetting = (v = {}) =>
  PUBLISH_SETTING_SURVEY_CODES.includes(v.survey_code) ||
  (v.survey_code === E3_SURVEY_CODE &&
    (v.e3_option === 1 || v.e3_option === 2));

// 未入力判定。0 は有効な値なので空扱いしない
//（公開選択の 0=非公開 / オプションの 0=組織診断のみ）。
export const isEmptyValue = (v) =>
  v === null ||
  v === undefined ||
  (typeof v === 'string' && v.trim() === '') ||
  (Array.isArray(v) && v.length === 0);

// required: 常に必須
// requiredWhen(value): その条件のとき必須（＝画面に出ているときは必須）
// keys: 1項目が複数のDBフィールドに対応する場合（日程など）
// note: 任意項目の既定動作。画面のバッジ横に出す
export const SEASON_FIELDS = [
  { key: 'season_name', label: 'シーズン名', required: true },
  { key: 'season_description', label: 'シーズン説明', required: true },
  { key: 't_name', label: '研修名', note: '研修を行わない場合は空欄で可' },
  { key: 'group_code', label: '企業選択', required: true },
  { key: 'survey_code', label: 'サーベイ選択', required: true },
  {
    key: 'question_set',
    label: '設問セット割り当て',
    note: '登録後に別画面で割り当て可',
  },
  {
    key: 'course_part_view_string',
    label: '表示領域選択記号',
    requiredWhen: (v) => COURSE_PART_VIEW_SURVEY_CODES.includes(v.survey_code),
  },
  {
    key: 'display_department',
    label: '表示所属',
    note: '未選択＝所属1（E3は所属3→2→1）',
  },
  {
    key: 'company_name_source',
    label: '会社名の表示元',
    note: '未選択＝グループ名',
  },
  {
    key: 'e3_option',
    label: 'オプション選択',
    requiredWhen: (v) => v.survey_code === E3_SURVEY_CODE,
  },
  {
    key: 'output_360_type',
    label: '公開選択（結果の表示方法）',
    requiredWhen: hasPublishSetting,
  },
  {
    key: 'output_360_date',
    label: '公開日',
    requiredWhen: (v) =>
      hasPublishSetting(v) && Number(v.output_360_type) === 2,
  },
  { key: 'name_360', label: '診断表示名', note: '未入力時はサーベイ種別名' },
  {
    key: 'survey_period',
    label: 'サーベイ回答日程',
    keys: ['survey_start_date', 'survey_end_date'],
    required: true,
    // 回答終了日は帳票の採点ロジック分岐にも使われるため（2026-01-01 前後で
    // 設問の反転採点が変わる）、未設定のまま運用されると点数がずれる。
  },
  { key: 'due_datetime', label: '回答期限', note: 'メール差込用・表示のみ' },
  {
    key: 'extended_due_datetime',
    label: '回答延長期限',
    note: 'メール差込用・表示のみ',
  },
  {
    key: 'training_period',
    label: '研修日程',
    keys: ['training_start_date', 'training_end_date'],
    note: '研修を行わない場合は空欄で可',
  },
];

// その項目が「いま必須か」
export const isFieldRequired = (field, value = {}) => {
  if (field.required) return true;
  if (field.requiredWhen) return !!field.requiredWhen(value);
  return false;
};

// その項目が未入力か（keys 指定時はどれか1つでも欠けていれば未入力）
export const isFieldFilled = (field, value = {}) => {
  const keys = field.keys || [field.key];
  return keys.every((k) => !isEmptyValue(value[k]));
};

// 登録前チェック。足りない項目のラベル配列を返す（空配列なら OK）
export const collectMissingLabels = (value = {}) =>
  SEASON_FIELDS.filter(
    (f) => isFieldRequired(f, value) && !isFieldFilled(f, value)
  ).map((f) => f.label);

// label 取得用（サーバーの ValidationError をラベルへ読み替える）
export const SERVER_FIELD_LABELS = SEASON_FIELDS.reduce((acc, f) => {
  (f.keys || [f.key]).forEach((k) => {
    acc[k] = f.label;
  });
  acc.group_id = '企業選択';
  acc.survey_id = 'サーベイ選択';
  return acc;
}, {});
